import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Search, MapPin, Palmtree } from 'lucide-react';
import { heroBanner } from '../mock';
import { useSettings } from '../store';
import { API } from '../api';

const HeroSearch = () => {
  const [query, setQuery] = useState('');
  const [destinations, setDestinations] = useState([]);
  const [showSug, setShowSug] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const nav = useNavigate();
  const settings = useSettings();
  const wrapRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/taxonomies?type=destination`).then((r) => {
      setDestinations(Array.isArray(r.data) ? r.data : []);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setShowSug(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const suggestions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return destinations.filter((d) => (d.name || '').toLowerCase().includes(q) || (d.slug || '').toLowerCase().includes(q)).slice(0, 8);
  }, [destinations, query]);

  const openDestination = (d) => {
    setShowSug(false);
    setQuery(d.name || '');
    nav(`/destination/${d.slug}`);
  };

  const doSearch = (q) => {
    const term = (q ?? query).trim();
    if (!term) return;
    // If the term exactly matches a destination name/slug → open its page.
    const match = destinations.find((d) => (d.name || '').toLowerCase() === term.toLowerCase() || (d.slug || '').toLowerCase() === term.toLowerCase());
    if (match) { openDestination(match); return; }
    nav(`/search?q=${encodeURIComponent(term)}`);
  };
  const onKey = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setShowSug(true); setHighlight((h) => Math.min(h + 1, suggestions.length - 1)); return; }
    if (e.key === 'ArrowUp') { e.preventDefault(); setHighlight((h) => Math.max(h - 1, 0)); return; }
    if (e.key === 'Enter') {
      if (showSug && highlight >= 0 && suggestions[highlight]) { openDestination(suggestions[highlight]); return; }
      doSearch();
      return;
    }
    if (e.key === 'Escape') { setShowSug(false); return; }
  };

  const domestic = settings.hero_chips_domestic || [];
  const international = settings.hero_chips_international || [];
  const heroImg = settings.hero_image || heroBanner;

  return (
    <section className="relative min-h-[480px] pt-20 pb-10 overflow-hidden bg-[#0d64a8]">
      {/* Background */}
      <div className="absolute inset-0">
        <img src={heroImg} alt="hero background" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0a2a4a]/70 via-[#0a2a4a]/40 to-[#0d64a8]/30" />
      </div>

      <div className="max-w-[1280px] mx-auto px-5 lg:px-8 pt-10 relative z-10">
        <h1 className="text-white text-4xl md:text-5xl font-bold tracking-tight mb-2 drop-shadow">
          {(() => {
            const heading = settings.hero_heading || 'Explore, Discover, Enjoy';
            const hw = (settings.hero_highlight_word || '').trim();
            const color = settings.hero_highlight_color || '#ffcc29';
            if (!hw) return heading;
            // Split on the first occurrence of the highlight word (case-insensitive)
            const idx = heading.toLowerCase().indexOf(hw.toLowerCase());
            if (idx === -1) return heading;
            const before = heading.slice(0, idx);
            const match = heading.slice(idx, idx + hw.length);
            const after = heading.slice(idx + hw.length);
            return (
              <>
                {before}
                <span style={{ color }} data-testid="hero-highlight">{match}</span>
                {after}
              </>
            );
          })()}
        </h1>
        {settings.hero_subheading ? (
          <p className="text-white/90 text-base mb-6 max-w-2xl drop-shadow">{settings.hero_subheading}</p>
        ) : (
          <div className="mb-6" />
        )}

        {/* Search Card - travelsera-branded width, mobile-safe */}
        <div className="bg-white rounded-lg shadow-2xl w-full max-w-[900px]">
          {/* Single active tab like the original site */}
          <div className="flex border-b border-gray-100 px-3">
            <button
              className="flex items-center gap-2 px-3 py-2.5 text-[13px] font-medium text-[#0d64a8] border-b-2 border-[#0d64a8] -mb-px"
              data-testid="hero-tab-holidays"
            >
              <Palmtree size={15} /> Holidays
            </button>
          </div>

          {/* Search row */}
          <div className="px-3 py-3 flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
            <div ref={wrapRef} className="flex-1 relative min-w-0">
              <div className="flex items-center gap-2 border border-gray-200 rounded px-2.5 py-2 hover:border-[#0d64a8] focus-within:border-[#0d64a8] transition min-w-0">
                <MapPin size={15} className="text-[#0d64a8] shrink-0" />
                <input
                  value={query}
                  onChange={(e) => { setQuery(e.target.value); setShowSug(true); setHighlight(-1); }}
                  onFocus={() => query.trim() && setShowSug(true)}
                  onKeyDown={onKey}
                  placeholder="Search Destinations"
                  className="flex-1 outline-none text-sm placeholder:text-gray-400 bg-transparent min-w-0"
                  data-testid="hero-search-input"
                  autoComplete="off"
                />
              </div>
              {showSug && suggestions.length > 0 && (
                <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-gray-200 rounded-md shadow-xl z-30 max-h-72 overflow-auto" data-testid="hero-search-suggestions">
                  {suggestions.map((d, i) => (
                    <button
                      key={d.slug}
                      type="button"
                      onMouseDown={(e) => { e.preventDefault(); openDestination(d); }}
                      onMouseEnter={() => setHighlight(i)}
                      className={`w-full flex items-center gap-2 text-left px-3 py-2 text-sm border-b border-gray-50 last:border-0 ${i === highlight ? 'bg-[#eef4f8] text-[#0d64a8]' : 'hover:bg-gray-50 text-gray-800'}`}
                      data-testid={`hero-search-suggestion-${d.slug}`}
                    >
                      <MapPin size={13} className="text-[#0d64a8]" />
                      <span className="font-medium">{d.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button
              onClick={() => doSearch()}
              className="bg-[#0d64a8] hover:bg-[#0a548e] text-white text-sm font-semibold px-4 py-2.5 rounded flex items-center justify-center gap-1.5 transition whitespace-nowrap"
              data-testid="hero-search-btn"
            >
              <Search size={14} />
              Search Holidays
            </button>
          </div>

          {/* Chips — International first (per admin ordering policy) */}
          <div className="px-3 pb-3 space-y-2">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[12px]" data-testid="hero-chips-international">
              <span className="text-gray-500 mr-0.5 shrink-0">International:</span>
              {international.map((name, i) => (
                <React.Fragment key={`i-${name}`}>
                  {i > 0 && <span className="text-gray-300">|</span>}
                  <button
                    onClick={() => doSearch(name)}
                    className="inline-flex items-center gap-1 text-gray-700 hover:text-[#0d64a8] transition"
                  >
                    <MapPin size={11} className="text-[#0d64a8]" />
                    <span className="border-b border-transparent hover:border-[#0d64a8]">{name}</span>
                  </button>
                </React.Fragment>
              ))}
            </div>
            <div className="h-px bg-gray-100" />
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[12px]" data-testid="hero-chips-domestic">
              <span className="text-gray-500 mr-0.5 shrink-0">Domestic:</span>
              {domestic.map((name, i) => (
                <React.Fragment key={`d-${name}`}>
                  {i > 0 && <span className="text-gray-300">|</span>}
                  <button
                    onClick={() => doSearch(name)}
                    className="inline-flex items-center gap-1 text-gray-700 hover:text-[#0d64a8] transition"
                  >
                    <MapPin size={11} className="text-[#0d64a8]" />
                    <span className="border-b border-transparent hover:border-[#0d64a8]">{name}</span>
                  </button>
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSearch;
